import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faSpinner } from "@fortawesome/free-solid-svg-icons";
import api from "../../api/http";
import "../../styles/produs/Rating.css";

const Rating = ({ productId, onRated }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [error, setError] = useState("");

  const isLoggedIn = Boolean(sessionStorage.getItem("access"));

  useEffect(() => {
    setFetched(false);
    setRating(0);
    setHover(0);
    setError("");
  }, [productId]);

  useEffect(() => {
    if (!isLoggedIn || !productId || fetched) return;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get(`/produs/ratings/${productId}/`);
        const r = Number(res?.data?.rating ?? 0);
        setRating(Number.isFinite(r) ? r : 0);
      } catch (err) {
        if (err?.response?.status === 404) setRating(0);
        else if (err?.response?.status === 401) setError("Sesiunea a expirat. Te rog loghează-te din nou.");
        else console.error("Error fetching user rating:", err);
      } finally {
        setLoading(false);
        setFetched(true);
      }
    };
    load();
  }, [isLoggedIn, productId, fetched]);

  const parseError = (err) => {
    const data = err?.response?.data;
    if (!data) return "Eroare la adăugarea rating-ului.";
    if (typeof data === "string") return data;
    if (typeof data.detail === "string") return data.detail;
    if (typeof data === "object") {
      return Object.entries(data)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`)
          .join("\n");
    }
    return "Eroare la adăugarea rating-ului.";
  };

  const handleRate = async (newRating) => {
    setError("");
    if (!isLoggedIn) { setError("Trebuie să vă autentificați pentru a evalua produsul."); return; }
    if (!productId || loading) return;
    try {
      setLoading(true);
      const res = await api.post("/produs/ratings/", { rating: newRating, produs: productId });
      if (res.status === 200 || res.status === 201) {
        setRating(newRating);
        if (typeof onRated === "function") onRated(newRating);
      }
    } catch (err) {
      if (err?.response?.status === 401) setError("Sesiunea a expirat. Te rog loghează-te din nou.");
      else setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  const visibleValue = useMemo(() => (hover ? hover : rating), [hover, rating]);

  const onKeyRate = (e, value) => {
    if (loading) return;
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleRate(value); }
  };

  const labels = ["", "Slab", "Mediu", "Bun", "Foarte bun", "Excelent"];

  return (
      <div className="rt-root">
        <p className="rt-title">Evaluează</p>

        <div
            className={`rt-stars ${loading ? "rt-stars--disabled" : ""}`}
            role="radiogroup"
            aria-label="Rating"
        >
          {[1, 2, 3, 4, 5].map((i) => (
              <button
                  key={i}
                  role="radio"
                  aria-checked={rating === i}
                  tabIndex={0}
                  className={`rt-star ${i <= visibleValue ? "rt-star--on" : ""}`}
                  onMouseEnter={() => !loading && setHover(i)}
                  onMouseLeave={() => !loading && setHover(0)}
                  onClick={() => handleRate(i)}
                  onKeyDown={(e) => onKeyRate(e, i)}
                  title={isLoggedIn ? `Evaluează ${i}` : "Loghează-te ca să evaluezi"}
              >
                <FontAwesomeIcon icon={faStar} className="rt-star-icon" />
                <span className="rt-star-num">{i}</span>
              </button>
          ))}
        </div>

        <div className="rt-hint-row">
          {loading ? (
              <span className="rt-loading">
            <FontAwesomeIcon icon={faSpinner} className="rt-spin" /> Se salvează…
          </span>
          ) : (
              <span className="rt-hint">
            {visibleValue > 0 ? labels[visibleValue] : (isLoggedIn ? "Selectează o stea" : "Autentifică-te pentru a evalua")}
          </span>
          )}
        </div>

        {error && <div className="rt-error">{error}</div>}
      </div>
  );
};

export default Rating;